import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Card, CircularProgress, Typography, useTheme } from '@mui/material';
import { AlertMessage, MainHeader, SnackBarMessage } from '@eposnow/ui-core';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { UserContext } from '../../../context/UserContext';
import { UIContext } from '../../../context/UIContext';
import useCategories from './hooks/useCategories';
import useProducts from './hooks/useProducts';
import useCatalogueImport from './hooks/useCatalogueImport';
import PageHeader from '../../../components/pageHeaderComponents/PageHeader';
import { StepContext } from '../../../context/StepContext';
import ErrorLoadingData from '../../../components/ErrorLoadingData';
import { SidekickTablesContextProvider } from './helpers/SidekickTablesContext';
import { Category, CategoryToCreate, Product, ProductCreateEdit } from './helpers/types';
import EditCategoryCard from './categories/components/EditCategoryCard';
import CategoriesWrapper from './categories/components/CategoriesWrapper';
import EditSidebar from './sharedComponents/EditSidebar';
import EditImportedProductCard from './products/components/EditImportedProductCard';
import useBusinessInformation from '../../../helperHooks/useBusinessInformation';
import ImportedProductsColumnsWrapper from './products/components/ImportedProductsColumnsWrapper';
import ImportedProductsWrapper from './products/components/ImportedProductsWrapper';
import UploadFileDialog from './sharedComponents/UploadFileDialog';
import SkipStepPopup from '../../../components/SkipStepPopup';
import ActionsContainer from '../../../components/ActionsContainer';
import { deepClone } from '../../../helpers/helpers';

const CatalogueImportSidekick = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { locale, user } = useContext(UserContext);
  const { isMobile } = useContext(UIContext);
  const { goToNextStep } = useContext(StepContext);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [isSingleObject, setIsSingleObject] = useState(false);
  const [productsForTable, setProductsForTable] = useState<any[]>(null);
  const [isEditSidebarOpen, setIsEditSidebarOpen] = useState(false);
  const [hasAddedProducts, setHasAddedProducts] = useState(false);
  const [isNoProductPopupOpen, setIsNoProductPopupOpen] = useState(false);
  const {
    currencySymbol,
    isLoading: isLoadingCurrency,
    isError,
    refetchBusinessInformation,
  } = useBusinessInformation();

  const {
    categories: categoriesData,
    isLoadingCategories,
    isAddingCategories,
    addCategories,
    errorAddingCategories,
    categoriesAddedSuccessMessage,
    setCategoriesAddedSuccessMessage,
  } = useCategories();

  const {
    addProducts,
    isAddingProducts,
    setProductsAddedSuccessMessage,
    productsAddedSuccessMessage,
    errorAddingProducts,
  } = useProducts();

  const {
    isLoadingData: isLoadingSidekickResponse,
    showErrorMessage,
    fileNotAMenu,
    importedCategories,
    importedProducts,
    callImportCatalogueFile,
    updateCatalogueCategories,
    updateCatalogueProducts,
  } = useCatalogueImport();

  const isLoadingData = useMemo(
    () => isLoadingCurrency || isLoadingSidekickResponse,
    [isLoadingCurrency, isLoadingSidekickResponse]
  );

  const catalogueCategories = useMemo(() => {
    if (importedCategories?.length > 0)
      return importedCategories.map((category, index) => ({ ...category, id: index }));
    return [];
  }, [importedCategories]);

  const catalogueProducts = useMemo(() => {
    if (importedProducts?.length > 0)
      return importedProducts.map((product, index) => ({ ...product, id: index }));
    return [];
  }, [importedProducts]);

  const noCatalogueContent = useMemo(
    () =>
      !importedCategories ||
      !importedProducts ||
      (importedCategories?.length === 0 && importedProducts?.length === 0) ||
      (importedCategories == null && importedProducts == null),
    [importedCategories, importedProducts]
  );

  const cancelUploadDialog = useCallback(() => {
    setUploadDialogOpen(false);
  }, []);

  const confirmUploadDialog = useCallback(
    (file) => {
      callImportCatalogueFile(file);
      setUploadDialogOpen(false);
    },
    [callImportCatalogueFile]
  );

  const skipCategories = () => {
    updateCatalogueCategories([]);
  };

  const callAddProducts = (newProducts: Product[], id?: number) => {
    if (catalogueProducts.length === 0 || newProducts?.length === 0) return;
    setIsSingleObject(id != null);
    setProductsAddedSuccessMessage('')
    if (id != null) {
      const selectedProduct: Product = deepClone(newProducts.find((row) => row.id === id));
      const newProduct: ProductCreateEdit = {
        salePrice: selectedProduct.salePriceIncTax,
        costPrice: 0,
        name: selectedProduct.name.replace(/[',<>#]/g, ''),
        description: selectedProduct.description.replace(/["'\\#]/g, ''),
        categoryId: selectedProduct.categoryId === -1 ? null : selectedProduct.categoryId,
        sellOnTill: true,
      };
      addProducts([newProduct]);
    } else {
      addProducts(
        newProducts.map((row) => ({
          salePrice: row.salePriceIncTax,
          costPrice: 0,
          name: row.name.replace(/[',<>#]/g, ''),
          description: row.description.replace(/["'\\#]/g, ''),
          categoryId: row.categoryId === -1 ? null : row.categoryId,
          sellOnTill: true,
        }))
      );
    }
  };

  const callAddCategories = (newCategories: Category[], id?: number) => {
    if (catalogueCategories.length === 0 || newCategories?.length === 0) return;
    setIsSingleObject(id != null);
    setCategoriesAddedSuccessMessage('');
    if (id != null) {
      const selectedCategory: Category = deepClone(newCategories.find((row) => row.id === id));
      const newCategory: CategoryToCreate = {
        Name: selectedCategory.name.replace(/[',<>#]/g, ''),
        IsWet: selectedCategory.isWet,
        ShowOnTill: true,
      };
      addCategories([newCategory]);
    } else {
      addCategories(
        newCategories.map((row) => ({
          Name: row.name.replace(/[',<>#]/g, ''),
          IsWet: row.isWet,
          ShowOnTill: true,
        }))
      );
    }
  };

  useEffect(() => {
    if (categoriesAddedSuccessMessage && !isSingleObject) {
      updateCatalogueCategories([]);
      setProductsForTable(null);
    }
  }, [categoriesAddedSuccessMessage]);

  useEffect(() => {
    if (productsAddedSuccessMessage && !isSingleObject) {
      setHasAddedProducts(true);
      if (!isSingleObject) {
        updateCatalogueProducts([]);
        setProductsForTable(null);
      }
    }
  }, [productsAddedSuccessMessage]);

  useEffect(() => {
    if (!importedProducts) return;

    if (catalogueProducts?.length === 0) setProductsForTable([]);

    const newProducts = catalogueProducts.map((product) => ({
      ...product,
      categoryId:
        parseInt(
          categoriesData?.find(
            (category) => category.label.toLowerCase() === product?.categoryName?.toLowerCase()
          )?.value,
          10
        ) || -1,
    }));

    setProductsForTable(newProducts);
  }, [catalogueProducts, categoriesData]);

  const handleNextStepClick = () => {
    if (hasAddedProducts) {
      // Products added go to the next step
      goToNextStep();
    } else {
      // Show the confirmation modal
      setIsNoProductPopupOpen(true);
    }
  };

  const handleClosePopup = () => {
    setIsNoProductPopupOpen(false);
  };

  const handleSkipInPopup = () => {
    handleClosePopup();
    goToNextStep();
  };

  return (
    <>
      <PageHeader omitTextHeader />
      {isError && <ErrorLoadingData action={refetchBusinessInformation} />}
      {!isError && (
        <>
          <MainHeader
            title={t('screens.products.sidekick.import.mainHeaderTitleImportMenu')}
            theme={theme}
            styles={{
              marginY: 3,
              ...(isMobile && {
                marginY: 0,
                paddingX: 1,
                paddingBottom: 1,
              }),
            }}
          >
            <Typography>
              {t('screens.products.sidekick.import.mainHeaderDescriptionImport')}
            </Typography>
          </MainHeader>
          {showErrorMessage && (
            <AlertMessage type="error" theme={theme} locale={locale} styles={{ marginY: 2 }}>
              <Typography variant="body2" sx={{ color: theme?.palette?.error?.['160p'] }}>
                {t('screens.products.sidekick.import.errorCreatingChat')}
              </Typography>
            </AlertMessage>
          )}
          {fileNotAMenu && (
            <AlertMessage type="error" theme={theme} locale={locale} styles={{ marginY: 2 }}>
              <Typography variant="body2" sx={{ color: theme?.palette?.error?.['160p'] }}>
                {t('screens.products.sidekick.import.errorFileNotAMenu')}
              </Typography>
            </AlertMessage>
          )}
          <Card
            elevation={0}
            sx={{
              marginTop: 4,
              marginBottom: 3,
              padding: 2,
              ...(isMobile && {
                marginBottom: 0,
                marginTop: 0,
                padding: 0,
                paddingBottom: 1,
                borderRadius: 0,
              }),
              ...(isEditSidebarOpen && {
                paddingRight: { xs: 0, sm: 0 },
              }),
            }}
          >
            <SnackBarMessage
              message={categoriesAddedSuccessMessage}
              setMessage={setCategoriesAddedSuccessMessage}
              isMobile={isMobile}
              theme={theme}
              locale={locale}
            />
            <SnackBarMessage
              message={productsAddedSuccessMessage}
              setMessage={setProductsAddedSuccessMessage}
              isMobile={isMobile}
              theme={theme}
              locale={locale}
            />
            {isLoadingData && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: 'calc(100vh - 405px)',
                  flexDirection: 'column',
                  ...(isLoadingSidekickResponse && {
                    gap: 3,
                  }),
                }}
              >
                {isLoadingSidekickResponse && (
                  <Typography>
                    {t('screens.products.sidekick.import.fileImportDialog.sidekickImportingFile')}
                  </Typography>
                )}
                <CircularProgress
                  aria-label={t('components.loading.loadingData')}
                  aria-live="polite"
                  aria-busy={isLoadingData}
                  data-qa-id="loading-icon"
                  sx={{ color: 'text.secondary' }}
                />
              </Box>
            )}
            {!isLoadingData && (
              <>
                <Box
                  sx={{
                    ...(noCatalogueContent && {
                      height: `calc(100vh - 405px ${errorAddingCategories ? '- 70px' : ''}  ${
                        errorAddingProducts ? '- 70px' : ''
                      } ${showErrorMessage ? '- 70px' : ''} ${fileNotAMenu ? '- 70px' : ''} )`,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      ...(isMobile && {
                        marginBottom: 2,
                        height: `calc(100vh - 315px ${errorAddingCategories ? '- 70px' : ''}  ${
                          errorAddingProducts ? '- 70px' : ''
                        } ${showErrorMessage ? '- 70px' : ''} ${fileNotAMenu ? '- 70px' : ''} )`,
                      }),
                    }),
                  }}
                >
                  {noCatalogueContent && (
                    <Box sx={{ textAlign: 'center' }}>
                      <Button
                        color="primary"
                        disableElevation
                        variant="contained"
                        startIcon={<CloudUploadIcon />}
                        onClick={() => setUploadDialogOpen(!uploadDialogOpen)}
                        data-qa-id="upload-file-button"
                      >
                        {t('screens.products.sidekick.import.uploadFile')}
                      </Button>
                    </Box>
                  )}
                  {catalogueCategories.length > 0 && (
                    <SidekickTablesContextProvider
                      data={catalogueCategories}
                      isLoading={isAddingCategories}
                      apiFunction={callAddCategories}
                      EditComponent={EditCategoryCard}
                      categories={categoriesData}
                      idKey="id"
                      setIsEditSidebarOpen={setIsEditSidebarOpen}
                    >
                      <>
                        <CategoriesWrapper
                          isLoading={isLoadingData}
                          openDialog={() => setUploadDialogOpen(!uploadDialogOpen)}
                          skipCategories={skipCategories}
                        />
                        <EditSidebar />
                      </>
                    </SidekickTablesContextProvider>
                  )}
                  {catalogueCategories.length === 0 && catalogueProducts.length > 0 && (
                    <SidekickTablesContextProvider
                      data={productsForTable}
                      isLoading={isAddingProducts}
                      apiFunction={callAddProducts}
                      EditComponent={EditImportedProductCard}
                      categories={categoriesData}
                      idKey="id"
                      setIsEditSidebarOpen={setIsEditSidebarOpen}
                    >
                      <>
                        <ImportedProductsColumnsWrapper>
                          <ImportedProductsWrapper
                            isLoading={isLoadingData}
                            currencySymbol={currencySymbol}
                            openDialog={() => setUploadDialogOpen(!uploadDialogOpen)}
                          />
                        </ImportedProductsColumnsWrapper>
                        <EditSidebar />
                      </>
                    </SidekickTablesContextProvider>
                  )}
                </Box>
                <UploadFileDialog
                  open={uploadDialogOpen}
                  confirmAction={confirmUploadDialog}
                  cancelAction={cancelUploadDialog}
                />

                {!noCatalogueContent && (
                  <Typography
                    fontSize="12px"
                    sx={{
                      pt: 1,
                      textAlign: 'center',
                      ...(isMobile && {
                        px: 1,
                        textWrap: 'balance',
                      }),
                    }}
                    data-qa-id="sidekick-warning-message"
                  >
                    {t('screens.products.sidekick.import.fileImportDialog.warningSidekick')}
                  </Typography>
                )}
              </>
            )}
          </Card>
          <SkipStepPopup
            open={isNoProductPopupOpen}
            handleClose={handleClosePopup}
            handleSkip={handleSkipInPopup}
            popupTitle={t('screens.products.sidekick.noProductsAddedTitle')}
            popupDescription={t('screens.products.sidekick.noProductsAddedDescription')}
            // Override button text
            cancelButtonText={t('actions.goBack')}
            skipButtonText={t('actions.continueAnyway')}
          />

          <ActionsContainer>
            <Button
              variant="outlined"
              sx={{
                margin: '0px',
                flexGrow: '0',
                height: '48px',
              }}
              onClick={() => {
                navigate('/products');
              }}
            >
              {t('actions.previous')}
            </Button>

            {!noCatalogueContent || (noCatalogueContent && hasAddedProducts) ? (
              <Button
                variant="contained"
                sx={{
                  margin: '0px',
                  flexGrow: '0',
                  height: '48px',
                }}
                onClick={handleNextStepClick}
              >
                {t('actions.next')}
              </Button>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '0.5rem',
                }}
              >
                <Button
                  variant="outlined"
                  sx={{
                    margin: '0px',
                    flexGrow: '0',
                    height: '48px',
                  }}
                  onClick={goToNextStep}
                >
                  {t('actions.skip')}
                </Button>
                <Button
                  variant="contained"
                  sx={{
                    margin: '0px',
                    flexGrow: '0',
                    height: '48px',
                  }}
                  onClick={() => navigate('/products-categories')}
                >
                  {t(`screens.products.sidekick.${isMobile ? 'mobileManualAdd' : 'manualAdd'}`)}
                </Button>
              </Box>
            )}
          </ActionsContainer>
        </>
      )}
    </>
  );
};

export default CatalogueImportSidekick;
